/**
 * Camera controller — owns pan + zoom state and wires input handlers.
 *
 * Phase 1 policy per CLIENT_DESIGN §5:
 *   - Free-pan: middle-mouse-button drag updates `camera.pan{X,Y}`.
 *     Arrow keys also pan (step = 64 iso pixels per keydown).
 *   - Zoom: mouse wheel cycles the 5 discrete `ZOOM_LEVELS` (0.5/0.75/1/1.5/2).
 *   - No lock-to-avatar follow mode in Phase 1 — that's 1.5 polish.
 *
 * The camera owns the `worldContainer`'s transform: pan becomes
 * `worldContainer.position` offset (relative to the viewport-centred
 * origin), zoom becomes `worldContainer.scale`. See `applyCameraTransform`.
 */

import { Container } from 'pixi.js';

import {
  DEFAULT_ZOOM_INDEX,
  ZOOM_LEVELS,
  clampZoomIndex,
  type CameraState,
} from './iso';

export interface CameraController {
  /** Current camera state — mutated in place as input events fire. */
  state: CameraState;
  /** Advance the zoom index (positive = zoom in, negative = zoom out). */
  zoomBy: (steps: number) => void;
  /** Snap to a specific zoom index. */
  setZoomIndex: (index: number) => void;
  /** Offset the pan by (dx, dy) in iso pixels (pre-zoom). */
  panBy: (dx: number, dy: number) => void;
  /** Apply the current camera state to a PixiJS container's transform. */
  apply: (worldContainer: Container, viewportW: number, viewportH: number) => void;
  /** Detach all listeners the controller installed on the DOM. */
  destroy: () => void;
}

const KEY_PAN_STEP = 64; // iso pixels per arrow-key press

export interface CameraControllerOptions {
  /** Target element for pointer + wheel + keyboard events. */
  target: HTMLElement;
  /** Optional starting zoom index (default: 1.0×). */
  initialZoomIndex?: number;
}

export function createCameraController(
  options: CameraControllerOptions,
): CameraController {
  const { target } = options;
  let zoomIndex = clampZoomIndex(
    options.initialZoomIndex ?? DEFAULT_ZOOM_INDEX,
  );

  const state: CameraState = {
    panX: 0,
    panY: 0,
    zoom: ZOOM_LEVELS[zoomIndex] ?? 1,
  };

  // ─── Panning via middle-mouse drag ────────────────────────────────────
  let dragging = false;
  let lastClientX = 0;
  let lastClientY = 0;

  const onPointerDown = (e: PointerEvent): void => {
    if (e.button !== 1) return; // middle-mouse only
    dragging = true;
    lastClientX = e.clientX;
    lastClientY = e.clientY;
    target.setPointerCapture(e.pointerId);
    e.preventDefault();
  };
  const onPointerMove = (e: PointerEvent): void => {
    if (!dragging) return;
    const dx = e.clientX - lastClientX;
    const dy = e.clientY - lastClientY;
    lastClientX = e.clientX;
    lastClientY = e.clientY;
    // Screen pixels map to world iso pixels inversely scaled by zoom —
    // panning the camera by (px, py) in screen space corresponds to moving
    // the world container by (-px, -py) / zoom in world space.
    state.panX -= dx / state.zoom;
    state.panY -= dy / state.zoom;
  };
  const onPointerUp = (e: PointerEvent): void => {
    if (!dragging) return;
    dragging = false;
    try {
      target.releasePointerCapture(e.pointerId);
    } catch {
      // ignore — capture may already be released if the pointer left
    }
  };

  target.addEventListener('pointerdown', onPointerDown);
  target.addEventListener('pointermove', onPointerMove);
  target.addEventListener('pointerup', onPointerUp);
  target.addEventListener('pointercancel', onPointerUp);

  // ─── Zooming via wheel ────────────────────────────────────────────────
  const onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    const dir = e.deltaY < 0 ? 1 : -1; // wheel-up = zoom in
    zoomIndex = clampZoomIndex(zoomIndex + dir);
    state.zoom = ZOOM_LEVELS[zoomIndex] ?? state.zoom;
  };
  target.addEventListener('wheel', onWheel, { passive: false });

  // ─── Arrow-key panning ────────────────────────────────────────────────
  const onKeyDown = (e: KeyboardEvent): void => {
    // Only absorb input when the target owns focus — text-entry elements
    // elsewhere must remain usable. CLIENT_DESIGN §8.5.
    if (document.activeElement !== target && !target.contains(document.activeElement as Node | null)) {
      return;
    }
    // GH#7 C3: arrow-key signs match the mouse-drag convention in
    // `onPointerMove` above — pressing ArrowUp shifts world contents up
    // on screen (pivot.y increases), so the avatar visually moves up,
    // matching the user's "drag the world along with the keypress"
    // intuition. Pre-fix the signs were inverted, producing the
    // operator-reported "all four arrow keys move the avatar in the
    // opposite screen direction" symptom on the J1 Firefox smoke.
    switch (e.key) {
      case 'ArrowUp':
        state.panY += KEY_PAN_STEP;
        break;
      case 'ArrowDown':
        state.panY -= KEY_PAN_STEP;
        break;
      case 'ArrowLeft':
        state.panX += KEY_PAN_STEP;
        break;
      case 'ArrowRight':
        state.panX -= KEY_PAN_STEP;
        break;
      default:
        return;
    }
    e.preventDefault();
  };
  target.addEventListener('keydown', onKeyDown);

  return {
    state,
    zoomBy(steps) {
      zoomIndex = clampZoomIndex(zoomIndex + steps);
      state.zoom = ZOOM_LEVELS[zoomIndex] ?? state.zoom;
    },
    setZoomIndex(index) {
      zoomIndex = clampZoomIndex(index);
      state.zoom = ZOOM_LEVELS[zoomIndex] ?? state.zoom;
    },
    panBy(dx, dy) {
      state.panX += dx;
      state.panY += dy;
    },
    apply(worldContainer, viewportW, viewportH) {
      worldContainer.position.set(viewportW / 2, viewportH / 2);
      worldContainer.scale.set(state.zoom);
      worldContainer.pivot.set(state.panX, state.panY);
    },
    destroy() {
      target.removeEventListener('pointerdown', onPointerDown);
      target.removeEventListener('pointermove', onPointerMove);
      target.removeEventListener('pointerup', onPointerUp);
      target.removeEventListener('pointercancel', onPointerUp);
      target.removeEventListener('wheel', onWheel);
      target.removeEventListener('keydown', onKeyDown);
    },
  };
}
